import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  Calendar, 
  Target, 
  TrendingUp, 
  Zap, 
  Heart, 
  Moon, 
  Wind, 
  Flame,
  CheckCircle2,
  Clock,
  ArrowLeft,
  Sparkles,
  Droplets,
  RefreshCw
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { startOfWeek, endOfWeek, subDays, format } from "date-fns";
import personalizedPlanJournal from "@/assets/personalized-plan-journal.jpg";

interface UserProfile {
  journey_stage?: string;
  symptoms?: string[];
  goals?: string[];
  knowledge_level?: string;
}

interface DailyTask {
  id: string;
  title: string;
  description: string;
  icon: any;
  duration: string;
  completed: boolean;
  category: string;
  priority?: string;
}

interface AIInsight {
  title: string;
  message: string;
  actionable: boolean;
}

interface WeeklyGoal {
  id: string;
  title: string;
  target: number;
  current: number;
  unit: string;
}

const PersonalPlan = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [generatingPlan, setGeneratingPlan] = useState(false);
  const [dailyTasks, setDailyTasks] = useState<DailyTask[]>([]);
  const [weeklyGoals, setWeeklyGoals] = useState<WeeklyGoal[]>([]);
  const [aiInsights, setAiInsights] = useState<AIInsight[]>([]);
  const [motivationalMessage, setMotivationalMessage] = useState<string>("");

  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        navigate("/auth");
        return;
      }

      const { data } = await supabase
        .from("profiles")
        .select("journey_stage, symptoms, goals, knowledge_level")
        .eq("user_id", user.id)
        .maybeSingle();

      if (data) {
        setProfile(data);
        await generateAIPlan(user.id, data);
      }
    } catch (error) {
      console.error("Error loading profile:", error);
      toast({
        title: "Error",
        description: "Failed to load your plan",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const generateAIPlan = async (userId: string, profileData: UserProfile) => {
    setGeneratingPlan(true);
    try {
      // Fetch real tracking data from last 7 days
      const sevenDaysAgo = format(subDays(new Date(), 7), 'yyyy-MM-dd');
      const today = format(new Date(), 'yyyy-MM-dd');

      // Get symptom logs
      const { data: symptomLogs } = await supabase
        .from('symptom_logs')
        .select('*')
        .eq('user_id', userId)
        .gte('log_date', sevenDaysAgo)
        .lte('log_date', today);

      // Get hot flash data
      const { data: hotFlashes } = await supabase
        .from('hot_flash_times')
        .select('*')
        .eq('user_id', userId)
        .gte('log_date', sevenDaysAgo)
        .lte('log_date', today);

      // Get bladder diary data
      const { data: bladderData } = await supabase
        .from('bladder_diary')
        .select('*')
        .eq('user_id', userId)
        .gte('log_date', sevenDaysAgo)
        .lte('log_date', today);

      // Calculate tracking stats
      const weekStart = format(startOfWeek(new Date()), 'yyyy-MM-dd');
      const uniqueDaysTracked = new Set(symptomLogs?.map(log => log.log_date) || []).size;
      const totalDaysTracking = new Set([
        ...(symptomLogs?.map(log => log.log_date) || []),
        ...(hotFlashes?.map(hf => hf.log_date) || []),
        ...(bladderData?.map(bd => bd.log_date) || [])
      ]).size;

      // Analyze symptom patterns
      const symptomSummary = (symptomLogs || []).reduce((acc, log) => {
        if (!acc[log.symptom_type]) {
          acc[log.symptom_type] = { count: 0, totalIntensity: 0, intensities: [] };
        }
        acc[log.symptom_type].count++;
        acc[log.symptom_type].totalIntensity += log.intensity;
        acc[log.symptom_type].intensities.push(log.intensity);
        return acc;
      }, {} as Record<string, { count: number; totalIntensity: number; intensities: number[] }>);

      const recentSymptomLogs = Object.entries(symptomSummary).map(([symptom, data]) => {
        const avgIntensity = data.totalIntensity / data.count;
        const trend = data.intensities.length >= 2 
          ? (data.intensities[data.intensities.length - 1] > data.intensities[0] ? "increasing" : "stable/decreasing")
          : "insufficient data";
        
        return {
          symptom,
          frequency: data.count,
          avgIntensity: Math.round(avgIntensity * 10) / 10,
          trend
        };
      });

      const hasBladderIssues = (bladderData || []).some(
        entry => entry.had_leakage || (entry.urgency_level && entry.urgency_level >= 4)
      );

      // Call AI function to generate personalized plan
      const { data: planData, error: planError } = await supabase.functions.invoke('generate-personalized-plan', {
        body: {
          symptoms: profileData.symptoms || [],
          goals: profileData.goals || [],
          recentSymptomLogs,
          trackingDays: totalDaysTracking,
          weeklyStats: {
            symptomsTracked: symptomLogs?.length || 0,
            daysTracked: uniqueDaysTracked,
            hotFlashCount: hotFlashes?.length || 0,
            bladderIssues: hasBladderIssues
          }
        }
      });

      if (planError) throw planError;

      // Map AI tasks to UI format
      const iconMap: Record<string, any> = {
        relief: Wind,
        sleep: Moon,
        mental: Heart,
        tracking: Target,
        learning: Sparkles,
        bladder: Droplets
      };

      const tasks: DailyTask[] = planData.dailyTasks.map((task: any, index: number) => ({
        id: String(index + 1),
        title: task.title,
        description: task.description,
        icon: iconMap[task.category] || Target,
        duration: task.priority === 'high' ? '10 min' : task.priority === 'medium' ? '5 min' : '3 min',
        completed: false,
        category: task.category,
        priority: task.priority
      }));

      setDailyTasks(tasks);
      setAiInsights(planData.insights || []);
      setMotivationalMessage(planData.motivationalMessage || "Keep up the great work!");

      // Generate real weekly goals based on actual data
      const realWeeklyGoals: WeeklyGoal[] = [
        {
          id: '1',
          title: 'Symptom Tracking Days',
          target: 7,
          current: uniqueDaysTracked,
          unit: 'days'
        },
        {
          id: '2',
          title: 'Total Symptoms Logged',
          target: 14,
          current: symptomLogs?.length || 0,
          unit: 'entries'
        }
      ];

      if ((hotFlashes?.length || 0) > 0) {
        realWeeklyGoals.push({
          id: '3',
          title: 'Hot Flash Episodes Tracked',
          target: 7,
          current: hotFlashes?.length || 0,
          unit: 'episodes'
        });
      }

      if ((bladderData?.length || 0) > 0) {
        realWeeklyGoals.push({
          id: '4',
          title: 'Bladder Diary Entries',
          target: 14,
          current: bladderData?.length || 0,
          unit: 'entries'
        });
      }

      setWeeklyGoals(realWeeklyGoals);

    } catch (error: any) {
      console.error("Error generating AI plan:", error);
      // Fallback to basic plan if AI fails
      generateBasicPlan(profileData);
      toast({
        title: "Using Basic Plan",
        description: "AI personalization unavailable, using profile-based plan",
      });
    } finally {
      setGeneratingPlan(false);
    }
  };

  const generateBasicPlan = (profileData: UserProfile) => {
    // Fallback to original logic
    const symptoms = profileData.symptoms || [];
    const tasks: DailyTask[] = [];
    
    if (symptoms.includes('hot_flashes')) {
      tasks.push({
        id: '1',
        title: '4-7-8 Breathing Exercise',
        description: 'Cool down with focused breathing',
        icon: Wind,
        duration: '5 min',
        completed: false,
        category: 'relief'
      });
    }
    
    if (symptoms.includes('sleep_issues')) {
      tasks.push({
        id: '2',
        title: 'Evening Wind-Down Routine',
        description: 'Prepare your body for restful sleep',
        icon: Moon,
        duration: '15 min',
        completed: false,
        category: 'sleep'
      });
    }
    
    tasks.push({
      id: '4',
      title: 'Track Your Symptoms',
      description: 'Log today\'s symptoms and patterns',
      icon: Target,
      duration: '3 min',
      completed: false,
      category: 'tracking'
    });

    setDailyTasks(tasks);
    setWeeklyGoals([
      { id: '1', title: 'Symptom Tracking Days', target: 7, current: 0, unit: 'days' }
    ]);
  };

  const regeneratePlan = async () => {
    if (!profile) return;
    
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    toast({
      title: "Regenerating Plan",
      description: "Analyzing your latest data...",
    });

    await generateAIPlan(user.id, profile);
  };

  const toggleTask = (taskId: string) => {
    setDailyTasks(prev => 
      prev.map(task => 
        task.id === taskId ? { ...task, completed: !task.completed } : task
      )
    );
    
    toast({
      title: "Task updated",
      description: "Keep up the great work!",
    });
  };

  const getCompletedTasksPercentage = () => {
    if (dailyTasks.length === 0) return 0;
    const completed = dailyTasks.filter(t => t.completed).length;
    return Math.round((completed / dailyTasks.length) * 100);
  };

  const navigateToTask = (task: DailyTask) => {
    switch (task.category) {
      case 'relief':
      case 'sleep':
      case 'mental':
        navigate('/sos-toolkit');
        break;
      case 'tracking':
        navigate('/tracker');
        break;
      case 'learning':
        navigate('/micro-learning');
        break;
      default:
        break;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading your personalized plan...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background relative">
      {/* Background Image with Fade */}
      <div 
        className="fixed inset-0 z-0"
        style={{
          backgroundImage: `url(${personalizedPlanJournal})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-background/95 via-background/90 to-background/95" />
      </div>

      {/* Content */}
      <div className="relative z-10">
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate("/")}
            className="mb-4"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Home
          </Button>
          
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-4xl font-bold text-foreground mb-2">Your Personalized Plan</h1>
              <p className="text-muted-foreground text-lg">
                {generatingPlan ? "Analyzing your tracking data..." : "AI-powered guidance based on your logged data"}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {profile?.journey_stage && (
                <Badge variant="secondary" className="capitalize text-base px-4 py-2">
                  {profile.journey_stage.replace('-', ' ')}
                </Badge>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={regeneratePlan}
                disabled={generatingPlan}
              >
                <RefreshCw className={`mr-2 h-4 w-4 ${generatingPlan ? 'animate-spin' : ''}`} />
                Refresh Plan
              </Button>
            </div>
          </div>
        </div>

        {/* Today's Overview */}
        <Card className="mb-8 border-2 border-primary/20">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-primary" />
                  Today's Progress
                </CardTitle>
                <CardDescription>
                  {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                </CardDescription>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold text-primary">{getCompletedTasksPercentage()}%</div>
                <div className="text-sm text-muted-foreground">Complete</div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Progress value={getCompletedTasksPercentage()} className="h-3" />
          </CardContent>
        </Card>

        <Tabs defaultValue="daily" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="daily">Daily Protocols</TabsTrigger>
            <TabsTrigger value="weekly">Weekly Goals</TabsTrigger>
            <TabsTrigger value="insights">Insights</TabsTrigger>
          </TabsList>

          {/* Daily Protocols Tab */}
          <TabsContent value="daily" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Today's Action Items</CardTitle>
                <CardDescription>
                  Complete these tasks to support your wellness journey
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {generatingPlan ? (
                  <>
                    {[1, 2, 3].map(i => (
                      <div key={i} className="flex items-center gap-4 p-4 rounded-lg border-2">
                        <Skeleton className="h-6 w-6" />
                        <Skeleton className="w-12 h-12 rounded-full" />
                        <div className="flex-1 space-y-2">
                          <Skeleton className="h-4 w-3/4" />
                          <Skeleton className="h-3 w-1/2" />
                        </div>
                        <Skeleton className="h-8 w-16" />
                      </div>
                    ))}
                  </>
                ) : dailyTasks.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Target className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p>Start tracking symptoms to get personalized recommendations</p>
                  </div>
                ) : (
                  dailyTasks.map((task) => {
                    const Icon = task.icon;
                    const priorityColor = task.priority === 'high' ? 'border-red-500/30' : task.priority === 'medium' ? 'border-orange-500/30' : 'border-border';
                    return (
                      <div
                        key={task.id}
                        className={`flex items-center gap-4 p-4 rounded-lg border-2 transition-all ${
                          task.completed 
                            ? 'bg-primary/5 border-primary/20' 
                            : `bg-background ${priorityColor} hover:border-primary/30`
                        }`}
                      >
                        <Checkbox
                          checked={task.completed}
                          onCheckedChange={() => toggleTask(task.id)}
                          className="h-6 w-6"
                        />
                        <div className="w-12 h-12 rounded-full bg-accent-soft flex items-center justify-center flex-shrink-0">
                          <Icon className="h-6 w-6 text-accent" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className={`font-semibold ${task.completed ? 'line-through text-muted-foreground' : 'text-foreground'}`}>
                              {task.title}
                            </h3>
                            {task.priority === 'high' && !task.completed && (
                              <Badge variant="destructive" className="text-xs">Priority</Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">{task.description}</p>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-1 text-sm text-muted-foreground">
                            <Clock className="h-4 w-4" />
                            {task.duration}
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigateToTask(task)}
                          >
                            Start
                          </Button>
                        </div>
                      </div>
                    );
                  })
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Weekly Goals Tab */}
          <TabsContent value="weekly" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>This Week's Goals</CardTitle>
                <CardDescription>
                  Track your progress towards weekly wellness targets
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {weeklyGoals.map((goal) => (
                  <div key={goal.id} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-foreground">{goal.title}</h3>
                      <span className="text-sm text-muted-foreground">
                        {goal.current} / {goal.target} {goal.unit}
                      </span>
                    </div>
                    <Progress 
                      value={(goal.current / goal.target) * 100} 
                      className="h-2"
                    />
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-primary/5 to-accent/5 border-primary/20">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-primary" />
                  Keep Going!
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">
                  You're making great progress this week. Consistency is key to lasting wellness.
                </p>
                <Button onClick={() => navigate("/progress")} variant="outline">
                  View Full Progress
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Insights Tab */}
          <TabsContent value="insights" className="space-y-4">
            {motivationalMessage && (
              <Card className="bg-gradient-to-br from-primary/10 to-accent/10 border-primary/30">
                <CardContent className="pt-6">
                  <p className="text-lg font-medium text-foreground">{motivationalMessage}</p>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader>
                <CardTitle>AI-Powered Insights</CardTitle>
                <CardDescription>
                  Based on your recent tracking data and patterns
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {generatingPlan ? (
                  <>
                    {[1, 2].map(i => (
                      <div key={i} className="p-4 rounded-lg border">
                        <Skeleton className="h-5 w-3/4 mb-2" />
                        <Skeleton className="h-16 w-full" />
                      </div>
                    ))}
                  </>
                ) : aiInsights.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Sparkles className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p>Track symptoms for a few days to receive personalized AI insights</p>
                  </div>
                ) : (
                  aiInsights.map((insight, index) => (
                    <div key={index} className="flex gap-4 p-4 rounded-lg bg-accent/10 border border-accent/20">
                      <Sparkles className="h-6 w-6 text-accent flex-shrink-0 mt-1" />
                      <div className="flex-1">
                        <h3 className="font-semibold mb-2">{insight.title}</h3>
                        <p className="text-sm text-muted-foreground mb-3">{insight.message}</p>
                        {insight.actionable && (
                          <Button size="sm" variant="outline" onClick={() => navigate('/tracker')}>
                            Take Action
                          </Button>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
      </div>
    </div>
  );
};

export default PersonalPlan;
